import { Paper, Tooltip } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import { type CSSProperties } from 'styled-components';
import { type ComponentDetails } from 'types/squares.type';
import { useTranslation } from 'react-i18next';

const getRiskColor = (type: string) => {
  switch (type) {
    case 'high':
      return '#ef4444';
    case 'med':
      return '#eab308';
    case 'low':
      return '#22c55e';
    default:
      return '#64748b';
  }
};

const RiskTooltipContent = ({
  components,
  riskType,
  enableDarkTheme,
}: {
  components: ComponentDetails[];
  riskType: 'high' | 'med' | 'low';
  enableDarkTheme: boolean;
}) => {
  const { t } = useTranslation();
  const popoverBg = enableDarkTheme ? '#1e293b' : '#ffffff';
  const textColor = enableDarkTheme ? '#f1f5f9' : '#1e293b';
  const borderColor = enableDarkTheme
    ? 'rgba(148, 163, 184, 0.2)'
    : 'rgba(226, 232, 240, 0.8)';

  return (
    <Paper
      sx={{
        maxWidth: '400px',
        padding: '12px',
        backgroundColor: popoverBg,
        border: `1px solid ${borderColor}`,
        boxShadow: enableDarkTheme
          ? '0 12px 32px rgba(0, 0, 0, 0.6), 0 4px 16px rgba(0, 0, 0, 0.4)'
          : '0 12px 32px rgba(0, 0, 0, 0.15), 0 4px 16px rgba(0, 0, 0, 0.08)',
      }}
    >
      {/* Header with risk type */}
      <div
        style={{
          fontSize: '13px',
          fontWeight: 600,
          color: getRiskColor(riskType),
          marginBottom: '12px',
          textTransform: 'capitalize',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
        }}
      >
        <div
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: getRiskColor(riskType),
          }}
        />
        {riskType} {t('risk_components')}
        {components.length})
      </div>

      {/* Component List */}
      <div
        style={{
          maxHeight: '240px',
          overflowY: 'auto',
          scrollbarWidth: 'thin',
          scrollbarColor: enableDarkTheme
            ? '#475569 transparent'
            : '#cbd5e1 transparent',
          padding: '0px 8px',
        }}
      >
        {components.length === 0 ? (
          <div
            style={{
              fontSize: '12px',
              color: textColor,
              padding: '8px 0',
              textAlign: 'center',
              fontStyle: 'italic',
            }}
          >
            {t('no_components_available')}
          </div>
        ) : (
          <table
            style={{
              width: '100%',
              borderCollapse: 'collapse', // keep rows aligned with full-width borders
              fontSize: '11px',
              fontWeight: 600,
              color: textColor,
              textTransform: 'capitalize',
            }}
          >
            <thead>
              <tr
                style={{
                  borderBottom: `1px solid ${
                    enableDarkTheme
                      ? 'rgba(148, 163, 184, 0.15)'
                      : 'rgba(226, 232, 240, 0.6)'
                  }`,
                }}
              >
                {['Name', 'Age in days', 'Criticality'].map((header) => (
                  <th
                    key={header}
                    style={{
                      textAlign: 'left',
                      padding: '6px 12px',
                      fontWeight: 600,
                      width: 'fit-content',
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {components.map((comp, index) => {
                const displayName =
                  comp.component === comp.sub_component
                    ? comp.sub_component
                    : `${comp.component} - ${comp.sub_component}`;

                return (
                  <tr
                    key={displayName ?? `comp-tooltip=${index}`}
                    style={{
                      borderBottom:
                        index < components.length - 1
                          ? `1px solid ${
                              enableDarkTheme
                                ? 'rgba(148, 163, 184, 0.15)'
                                : 'rgba(226, 232, 240, 0.6)'
                            }`
                          : 'none',
                    }}
                  >
                    <td
                      style={{
                        fontWeight: 500,
                        color: textColor,
                        lineHeight: 1.3,
                        padding: '8px 12px',
                        textTransform: 'capitalize',
                      }}
                    >
                      {displayName}
                    </td>
                    <td
                      style={{
                        fontWeight: 500,
                        color: textColor,
                        lineHeight: 1.3,
                        padding: '8px 12px',
                        textTransform: 'capitalize',
                      }}
                    >
                      {comp.age_in_days ? comp.age_in_days : 'WO Not available'}
                    </td>
                    <td>
                      <p
                        style={{
                          fontSize: '9px',
                          backgroundColor: getRiskColor(comp.criticality),
                          color: 'white',
                          padding: '6px 12px',
                          borderRadius: '12px',
                          fontWeight: 600,
                          textTransform: 'capitalize',
                          letterSpacing: '0.025em',
                          textAlign: 'center',
                          margin: 0,
                          width: 'fit-content',
                        }}
                      >
                        {comp.criticality}
                      </p>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Paper>
  );
};

export const RiskStatBlockWithTooltip = ({
  value,
  label,
  type,
  components,
}: {
  value: number | string | undefined;
  label: string;
  type: 'high' | 'med' | 'low';
  components: ComponentDetails[];
}) => {
  const { enableDarkTheme } = useAppTheme();

  //   stylings

  const blockStyle: CSSProperties = {
    flexGrow: 1,
    textAlign: 'center',
    padding: '6px 4px',
    borderRadius: '8px',
    cursor: components.length > 0 ? 'pointer' : 'default',
    transition: 'all 0.2s ease',
    position: 'relative',
    transform: 'translateY(0)',
    boxShadow: enableDarkTheme
      ? '0 2px 6px rgba(0,0,0,0.2)'
      : '0 2px 6px rgba(0,0,0,0.08)',
  };

  const valueStyle: CSSProperties = {
    fontSize: '1.1rem',
    fontWeight: 700,
    lineHeight: 1,
    margin: 0,
  };

  const labelStyle: CSSProperties = {
    fontSize: '0.6rem',
    fontWeight: 600,
    letterSpacing: '0.025em',
    textTransform: 'capitalize',
    margin: '3px 0 0 0',
    opacity: 0.9,
  };

  if (type === 'high') {
    blockStyle.background = enableDarkTheme
      ? 'linear-gradient(135deg, rgba(239, 68, 68, 0.15), rgba(220, 38, 38, 0.08))'
      : 'linear-gradient(135deg, rgba(239, 68, 68, 0.12), rgba(239, 68, 68, 0.06))';
    blockStyle.border = `1px solid rgba(239, 68, 68, ${
      enableDarkTheme ? '0.3' : '0.2'
    })`;
    valueStyle.color = enableDarkTheme ? '#f87171' : '#dc2626';
    labelStyle.color = enableDarkTheme ? '#fca5a5' : '#ef4444';
  } else if (type === 'med') {
    blockStyle.background = enableDarkTheme
      ? 'linear-gradient(135deg, rgba(234, 179, 8, 0.15), rgba(217, 119, 6, 0.08))'
      : 'linear-gradient(135deg, rgba(234, 179, 8, 0.12), rgba(234, 179, 8, 0.06))';
    blockStyle.border = `1px solid rgba(234, 179, 8, ${
      enableDarkTheme ? '0.3' : '0.2'
    })`;
    valueStyle.color = enableDarkTheme ? '#fbbf24' : '#d97706';
    labelStyle.color = enableDarkTheme ? '#fcd34d' : '#eab308';
  } else if (type === 'low') {
    blockStyle.background = enableDarkTheme
      ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(22, 163, 74, 0.08))'
      : 'linear-gradient(135deg, rgba(34, 197, 94, 0.12), rgba(34, 197, 94, 0.06))';
    blockStyle.border = `1px solid rgba(34, 197, 94, ${
      enableDarkTheme ? '0.3' : '0.2'
    })`;
    valueStyle.color = enableDarkTheme ? '#4ade80' : '#16a34a';
    labelStyle.color = enableDarkTheme ? '#6ee7b7' : '#22c55e';
  }

  return (
    <Tooltip
      title={
        <RiskTooltipContent
          components={components}
          riskType={type}
          enableDarkTheme={enableDarkTheme}
        />
      }
      arrow
      placement="bottom"
      componentsProps={{
        tooltip: {
          sx: {
            padding: 0,
            backgroundColor: 'transparent',
            boxShadow: 'none',
            maxWidth: '500px',
          },
        },
      }}
      enterDelay={200}
    >
      <div style={blockStyle}>
        <p style={valueStyle}>{value}</p>
        <p style={labelStyle}>{label}</p>
      </div>
    </Tooltip>
  );
};
